import type {TreeOption} from 'naive-ui';

/**
 * 把装配体正式基准对象节点合并到 `replaceTree` 权威树。
 *
 * 设计说明：
 * 1. 单根装配树把用户基准对象放在默认基准槽位之后、组件层级之前，保持 FeatureManager 系统项顺序稳定；
 * 2. 没有默认基准槽位时追加到根节点末尾，避免臆测未知外部树的系统节点类型；
 * 3. 多根外部树缺少唯一装配体宿主，保留旧兼容策略，把基准对象作为顶层前置节点；
 * 4. 合并过程只复制树数据，不修改提供者持有的原始节点。
 *
 * @param replaceTreeData 外部装配体权威树。
 * @param datumNodes 已完成创建顺序排序的装配体基准对象节点。
 * @returns 合并后的新树。
 */
export function mergeAssemblyDatumReferenceTree(
    replaceTreeData: readonly TreeOption[],
    datumNodes: readonly TreeOption[],
): TreeOption[] {
    // 空投影直接复制外部树，避免把 provider 持有的数据暴露为可变引用。
    if (datumNodes.length === 0) {
        return [...replaceTreeData];
    }

    // 多根外部树没有唯一宿主，只能保留既有顶层兼容布局。
    const firstRoot = replaceTreeData[0];
    if (replaceTreeData.length !== 1 || !firstRoot) {
        return [...datumNodes, ...replaceTreeData];
    }

    const existingChildren: TreeOption[] = Array.isArray(firstRoot.children)
        ? firstRoot.children as TreeOption[]
        : [];
    // 用户基准几何必须位于最后一个默认基准槽位之后，不能打乱历史记录和默认基准区。
    const lastDatumSlotIndex = existingChildren.reduce((resolvedIndex, child, index) => (
        child.type === 'glb-datum-slot' ? index : resolvedIndex
    ), -1);
    const insertionIndex = lastDatumSlotIndex >= 0
        ? lastDatumSlotIndex + 1
        : existingChildren.length;

    // 只克隆根节点及 children 数组，保留外部装配树内部节点的稳定身份和绑定元数据。
    return [{
        ...firstRoot,
        children: [
            ...existingChildren.slice(0, insertionIndex),
            ...datumNodes,
            ...existingChildren.slice(insertionIndex),
        ],
        isLeaf: false,
    }];
}
